import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/navigation/Header';
import Icon from '../../components/AppIcon';
import LeadPipelineCard from './components/LeadPipelineCard';
import LeadFunnelVisualization from './components/LeadFunnelVisualization';
import LeadScoringTable from './components/LeadScoringTable';
import ConversionMetricsPanel from './components/ConversionMetricsPanel';

const SalesLeadTrackingDashboard = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStage, setFilterStage] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');

  // Pipeline overview metrics
  const pipelineMetrics = {
    totalLeads: 127,
    qualifiedProspects: 45,
    conversionRate: 35.4,
    revenuePipeline: 2450000 // PNG Kina
  };

  // Sales funnel stages with lead distribution
  const funnelStages = [
    { stage: 'Awareness', leads: 127, percentage: 100, color: 'bg-blue-500' },
    { stage: 'Interest', leads: 89, percentage: 70, color: 'bg-indigo-500' },
    { stage: 'Consideration', leads: 62, percentage: 49, color: 'bg-purple-500' },
    { stage: 'Intent', leads: 45, percentage: 35, color: 'bg-pink-500' },
    { stage: 'Decision', leads: 28, percentage: 22, color: 'bg-emerald-500' }
  ];

  // Lead scoring data with priority indicators
  const leadsData = [
    {
      id: 1,
      company: 'PNG Department of Finance',
      contact: 'John Kila',
      email: 'j.kila@finance.gov.pg',
      leadScore: 92,
      priority: 'hot',
      stage: 'Intent',
      source: 'Website Demo',
      assignedTo: 'Sarah M.',
      nextAction: 'Schedule product demo',
      lastContact: '2026-01-18',
      estimatedValue: 180000
    },
    {
      id: 2,
      company: 'Provincial Health Authority',
      contact: 'Mary Johnson',
      email: 'm.johnson@health.gov.pg',
      leadScore: 78,
      priority: 'warm',
      stage: 'Consideration',
      source: 'LinkedIn Outreach',
      assignedTo: 'David K.',
      nextAction: 'Send pricing proposal',
      lastContact: '2026-01-17',
      estimatedValue: 120000
    },
    {
      id: 3,
      company: 'National Education Board',
      contact: 'Peter Wilson',
      email: 'p.wilson@education.gov.pg',
      leadScore: 85,
      priority: 'hot',
      stage: 'Decision',
      source: 'Referral',
      assignedTo: 'Sarah M.',
      nextAction: 'Contract negotiation',
      lastContact: '2026-01-19',
      estimatedValue: 250000
    },
    {
      id: 4,
      company: 'Port Moresby City Council',
      contact: 'Lisa Brown',
      email: 'l.brown@pmcc.gov.pg',
      leadScore: 65,
      priority: 'warm',
      stage: 'Interest',
      source: 'Trade Show',
      assignedTo: 'Michael T.',
      nextAction: 'Follow-up call',
      lastContact: '2026-01-16',
      estimatedValue: 95000
    },
    {
      id: 5,
      company: 'PNG Customs Service',
      contact: 'James Anderson',
      email: 'j.anderson@customs.gov.pg',
      leadScore: 45,
      priority: 'cold',
      stage: 'Awareness',
      source: 'Cold Email',
      assignedTo: 'David K.',
      nextAction: 'Send introduction email',
      lastContact: '2026-01-15',
      estimatedValue: 75000
    }
  ];

  // Conversion tracking metrics
  const conversionMetrics = {
    awarenessToInterest: 70.1,
    interestToConsideration: 69.7,
    considerationToIntent: 72.6,
    intentToDecision: 62.2,
    overallConversion: 22.0,
    bottleneck: 'Intent to Decision',
    recommendation: 'Focus on addressing pricing concerns and ROI demonstration'
  };

  // Filter leads based on search and filters
  const filteredLeads = leadsData?.filter(lead => {
    const matchesSearch = lead?.company?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
                         lead?.contact?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    const matchesStage = filterStage === 'all' || lead?.stage === filterStage;
    const matchesPriority = filterPriority === 'all' || lead?.priority === filterPriority;
    return matchesSearch && matchesStage && matchesPriority;
  });

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'hot': return 'text-red-600 bg-red-50 border-red-200';
      case 'warm': return 'text-amber-600 bg-amber-50 border-amber-200';
      case 'cold': return 'text-blue-600 bg-blue-50 border-blue-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const formatCurrency = (amount) => {
    return `K${amount?.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-900">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Sales Lead Tracking Dashboard
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Pre-launch customer acquisition management with lead scoring and conversion analytics
          </p>
        </div>

        {/* Pipeline Overview Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <LeadPipelineCard
            title="Total Leads"
            value={pipelineMetrics?.totalLeads}
            icon="Users"
            color="blue"
          />
          <LeadPipelineCard
            title="Qualified Prospects"
            value={pipelineMetrics?.qualifiedProspects}
            icon="UserCheck"
            color="emerald"
          />
          <LeadPipelineCard
            title="Conversion Rate"
            value={`${pipelineMetrics?.conversionRate}%`}
            icon="TrendingUp"
            color="purple"
          />
          <LeadPipelineCard
            title="Revenue Pipeline"
            value={formatCurrency(pipelineMetrics?.revenuePipeline)}
            icon="DollarSign"
            color="amber"
          />
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
          {/* Sales Funnel Visualization */}
          <div className="lg:col-span-2">
            <LeadFunnelVisualization stages={funnelStages} />
          </div>

          {/* Conversion Metrics */}
          <div className="lg:col-span-1">
            <ConversionMetricsPanel metrics={conversionMetrics} />
          </div>
        </div>

        {/* Lead Scoring Table */}
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700">
          <div className="p-6 border-b border-gray-200 dark:border-slate-700">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Lead Scoring & Management
              </h2>
              
              {/* Filters */}
              <div className="flex flex-wrap gap-3">
                <div className="relative">
                  <Icon name="Search" size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search leads..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e?.target?.value)}
                    className="pl-10 pr-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-transparent dark:bg-slate-700 dark:text-white"
                  />
                </div>
                
                <select
                  value={filterStage}
                  onChange={(e) => setFilterStage(e?.target?.value)}
                  className="px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-transparent dark:bg-slate-700 dark:text-white"
                >
                  <option value="all">All Stages</option>
                  <option value="Awareness">Awareness</option>
                  <option value="Interest">Interest</option>
                  <option value="Consideration">Consideration</option>
                  <option value="Intent">Intent</option>
                  <option value="Decision">Decision</option>
                </select>
                
                <select
                  value={filterPriority}
                  onChange={(e) => setFilterPriority(e?.target?.value)}
                  className="px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-transparent dark:bg-slate-700 dark:text-white"
                >
                  <option value="all">All Priorities</option>
                  <option value="hot">Hot Leads</option>
                  <option value="warm">Warm Prospects</option>
                  <option value="cold">Cold Leads</option>
                </select>
              </div>
            </div>
          </div>

          <LeadScoringTable 
            leads={filteredLeads} 
            getPriorityColor={getPriorityColor}
            formatCurrency={formatCurrency}
          />
        </div>

        {/* Export Actions */}
        <div className="mt-6 flex justify-end gap-3">
          <button className="px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors flex items-center gap-2">
            <Icon name="Download" size={16} />
            Export Lead Report
          </button>
          <button className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 transition-colors flex items-center gap-2">
            <Icon name="Plus" size={16} />
            Add New Lead
          </button>
        </div>
      </div>
    </div>
  );
};

export default SalesLeadTrackingDashboard;